import { useState } from 'react';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input, InputLabel, InputGroup } from '../../../components/ui';

export default function CalendarSubscribe({ calendar_url, has_token }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleGenerateToken = () => {
    router.post('/teacher/calendar/generate_token');
  };

  const handleRegenerateToken = () => {
    if (confirm('Regenerate your calendar link? This will invalidate any existing subscriptions using the old link.')) {
      router.post('/teacher/calendar/regenerate_token');
    }
  };

  const handleCopy = async () => {
    if (!calendar_url) return;
    try {
      await navigator.clipboard.writeText(calendar_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.getElementById('calendar-url');
      if (input) {
        input.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  return (
    <TeacherShell scrollTitle={t('teacherCalendar.subscribe')}>
      <TeacherPageHeader
        title={t('teacherCalendar.subscribeTitle')}
        backLink="/teacher/calendar"
        backLabel={t('teacherCalendar.title')}
      />

      <div className="max-w-2xl space-y-6">
        <Card className="p-6">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('teacherCalendar.syncDescription')}
          </p>

          {has_token && calendar_url ? (
            <div className="space-y-4">
              <InputGroup>
                <InputLabel htmlFor="calendar-url">{t('profile.feedUrl')}</InputLabel>
                <div className="flex gap-2">
                  <Input
                    id="calendar-url"
                    value={calendar_url}
                    readOnly
                    onClick={(e) => e.target.select()}
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCopy}
                    className="flex-shrink-0"
                  >
                    {copied ? t('common.copied') : t('common.copy')}
                  </Button>
                </div>
              </InputGroup>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerateToken}
                >
                  {t('profile.regenerateLink')}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowHelp(!showHelp)}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {showHelp ? t('teacherCalendar.hideInstructions') : t('teacherCalendar.howToSubscribe')}
                </button>
              </div>

              <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t('teacherCalendar.tokenWarning')}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('teacherCalendar.generateHelp')}
              </p>
              <Button type="button" variant="primary" onClick={handleGenerateToken}>
                {t('profile.generateCalendarLink')}
              </Button>
            </div>
          )}
        </Card>

        {/* How to subscribe — expandable */}
        {showHelp && (
          <Card className="p-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
              {t('teacherCalendar.howToSubscribeTitle')}
            </h3>
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {t('teacherCalendar.appleCal')}
                </h4>
                <ol className="text-sm text-gray-600 dark:text-gray-400 list-decimal list-inside space-y-1">
                  <li>{t('teacherCalendar.appleStep1')}</li>
                  <li>{t('teacherCalendar.appleStep2')}</li>
                  <li>{t('teacherCalendar.appleStep3')}</li>
                  <li>{t('teacherCalendar.appleStep4')}</li>
                </ol>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {t('teacherCalendar.googleCal')}
                </h4>
                <ol className="text-sm text-gray-600 dark:text-gray-400 list-decimal list-inside space-y-1">
                  <li>{t('teacherCalendar.googleStep1')}</li>
                  <li>{t('teacherCalendar.googleStep2')}</li>
                  <li>{t('teacherCalendar.googleStep3')}</li>
                  <li>{t('teacherCalendar.googleStep4')}</li>
                </ol>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {t('teacherCalendar.outlookCal')}
                </h4>
                <ol className="text-sm text-gray-600 dark:text-gray-400 list-decimal list-inside space-y-1">
                  <li>{t('teacherCalendar.outlookStep1')}</li>
                  <li>{t('teacherCalendar.outlookStep2')}</li>
                  <li>{t('teacherCalendar.outlookStep3')}</li>
                  <li>{t('teacherCalendar.outlookStep4')}</li>
                </ol>
              </div>
            </div>
          </Card>
        )}
      </div>
    </TeacherShell>
  );
}
